import React from 'react';

// ─── BUTTON (Primary / Secondary / Danger) ────────────────────────────────────
export function Button({ comp }) {
  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: comp.borderRadius || 7,
      background: comp.bg,
      color: comp.color,
      fontWeight: comp.fontWeight || 600,
      fontSize: comp.fontSize || 14,
      border: comp.borderWidth
        ? `${comp.borderWidth}px ${comp.borderStyle || 'solid'} ${comp.borderColor || 'transparent'}`
        : 'none',
      cursor: 'pointer',
    }}>
      {comp.label}
    </div>
  );
}

// ─── TEXT BLOCK ──────────────────────────────────────────────────────────────
export function TextBlock({ comp }) {
  const sizeMap = { h1: 32, h2: 24, h3: 18, body: 14, small: 12 };
  const weightMap = { h1: 700, h2: 700, h3: 600, body: 400, small: 400 };
  const fs = comp.fontSize || sizeMap[comp.variant] || 14;
  const fw = comp.fontWeight || weightMap[comp.variant] || 400;

  // vertical align mapping
  const vAlignMap = { top: 'flex-start', center: 'center', bottom: 'flex-end' };
  const vAlign = vAlignMap[comp.verticalAlign] || 'center';

  // build text-decoration value supporting multiple decorations
  const decorations = [];
  if (comp.underline) decorations.push('underline');
  if (comp.strikethrough) decorations.push('line-through');

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      display: 'flex', alignItems: vAlign,
    }}>
      <div style={{
        fontSize: fs, fontWeight: fw,
        color: comp.color || '#111827',
        lineHeight: comp.lineHeight || 1.6,
        textAlign: comp.align || 'left',
        letterSpacing: comp.letterSpacing ? `${comp.letterSpacing}em` : 'normal',
        fontStyle: comp.italic ? 'italic' : 'normal',
        textDecoration: decorations.length ? decorations.join(' ') : 'none',
        whiteSpace: 'pre-wrap',
        width: '100%',
        wordBreak: 'break-word',
      }}>
        {comp.content || 'Text block'}
      </div>
    </div>
  );
}

// ─── ALERT BOX ───────────────────────────────────────────────────────────────
export function AlertBox({ comp }) {
  const variants = {
    info:    { bg: '#EFF4FF', br: '#BFCFFE', ic: 'ℹ', cl: '#2563EB' },
    success: { bg: '#ECFDF5', br: '#6EE7B7', ic: '✓', cl: '#059669' },
    warning: { bg: '#FFFBEB', br: '#FCD34D', ic: '⚠', cl: '#D97706' },
    error:   { bg: '#FEF2F2', br: '#FCA5A5', ic: '✕', cl: '#DC2626' },
  };
  const v = variants[comp.variant] || variants.info;

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      background: v.bg,
      border: `1px solid ${v.br}`,
      borderRadius: comp.borderRadius || 8,
      padding: `${comp.padV ?? 13}px ${comp.padH ?? 15}px`,
      display: 'flex', gap: 10, alignItems: 'flex-start',
    }}>
      {comp.showIcon && (
        <span style={{ fontSize: 16, color: v.cl, flexShrink: 0, marginTop: 1 }}>{v.ic}</span>
      )}
      <div>
        {comp.title && (
          <div style={{
            fontSize: comp.titleFontSize || 14, fontWeight: 600,
            color: '#111827', marginBottom: 3,
          }}>
            {comp.title}
          </div>
        )}
        <div style={{ fontSize: comp.msgFontSize || 13, color: '#6B7280', lineHeight: 1.5 }}>
          {comp.message}
        </div>
      </div>
    </div>
  );
}

// ─── EMPTY STATE ─────────────────────────────────────────────────────────────
export function EmptyState({ comp }) {
  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 10,
      background: comp.bg || '#fff',
      borderRadius: comp.borderRadius || 10,
      border: comp.borderEnabled
        ? `${comp.borderWidth || 1}px ${comp.borderStyle || 'dashed'} ${comp.borderColor || '#CDD0D8'}`
        : 'none',
    }}>
      <div style={{ fontSize: comp.iconSize || 40, color: comp.iconColor || '#9CA3AF' }}>
        {comp.icon || '◌'}
      </div>
      <div style={{ fontSize: comp.titleFontSize || 16, fontWeight: 600, color: comp.titleColor || '#111827' }}>
        {comp.title}
      </div>
      <div style={{ fontSize: comp.msgFontSize || 13, color: comp.msgColor || '#6B7280' }}>
        {comp.message}
      </div>
      {comp.action && (
        <div style={{
          padding: `7px 19px`, borderRadius: comp.actionRadius || 7,
          background: comp.actionBg || '#2563EB', color: comp.actionColor || '#fff',
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>
          {comp.action}
        </div>
      )}
    </div>
  );
}

// ─── IMAGE BLOCK ─────────────────────────────────────────────────────────────
export function ImageBlock({ comp }) {
  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      borderRadius: comp.borderRadius || 10,
      display: 'flex', flexDirection: 'column',
      background: '#F0F2F5',
    }}>
      {comp.src ? (
        <img
          src={comp.src} alt={comp.alt}
          style={{ width: '100%', flex: 1, objectFit: comp.objectFit || 'cover' }}
        />
      ) : (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 6, color: '#9CA3AF',
        }}>
          <span style={{ fontSize: 28 }}>⬜</span>
          <span style={{ fontSize: 12 }}>Add image URL</span>
        </div>
      )}
      {comp.showCaption && comp.caption && (
        <div style={{
          padding: `6px 11px`,
          fontSize: comp.capFontSize || 12, color: comp.capColor || '#6B7280',
          textAlign: comp.capAlign || 'center', background: comp.capBg || '#fff',
        }}>
          {comp.caption}
        </div>
      )}
    </div>
  );
}

// ─── RICH HTML ───────────────────────────────────────────────────────────────
export function RichHtml({ comp }) {
  return (
    <div
      style={{
        width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box',
        padding: comp.padding || 16,
      }}
      dangerouslySetInnerHTML={{ __html: comp.html || '' }}
    />
  );
}
